import { Helmet } from "react-helmet-async";
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import HomeNav from '../HomeNav';
import HomeFooter from '../HomeFooter';
import GenieEmbed from '@/components/widget/GenieEmbed';
import { getCanonicalUrl } from "@/config/site";
import { createHowToSchema, createOrganizationSchema } from "@/config/seo";
import { 
  Brain, 
  MessageSquare, 
  Globe, 
  QrCode, 
  BarChart3, 
  Palette,
  Database,
  Target,
  Link,
  Code,
  Smartphone,
  Mail,
  ArrowRight,
  CheckCircle2,
  Settings,
  Users,
  FileText,
  Mic,
  Languages,
  Clock,
  Shield
} from 'lucide-react';

const HowGenieWorks = () => {
  const navigate = useNavigate();
  const description =
    "See how Help Genie works from setup to deployment, including voice AI configuration, knowledge bases, embeds, phone lines and analytics.";
  const organizationSchema = createOrganizationSchema();
  const howToSchema = createHowToSchema("How Help Genie Works", description, [
    { name: "Create your Genie", text: "Set up your Genie with voice-guided configuration and choose the right personality." },
    { name: "Add knowledge", text: "Upload content, FAQs and documentation so your Genie can answer accurately." },
    { name: "Customize the experience", text: "Tune branding, prompts, goals and lead capture behavior." },
    { name: "Deploy everywhere", text: "Launch your Genie on the web, by phone, through links and via QR codes." },
  ]);

  const setupSteps = [
    {
      number: "1",
      title: "Create Your Genie",
      description: "Use voice to set up your genie instantly",
      icon: Brain,
      features: [
        "Voice-guided setup wizard",
        "Choose voice and set personality",
        "Customize around your requirements"
      ],
      image: "/optimized/replicate-prediction-va4hh5y8vnrm80cttzbafvv2c0.webp"
    },
    {
      number: "2",
      title: "Add Knowledge",
      description: "Train your Genie with your content",
      icon: Database,
      features: [
        "Upload PDFs and documents",
        "Import website content",
        "Add extensive documentation and guidelines",
        "Add FAQs and responses"
      ],
      image: "/home-page-imagery/replicate-prediction-amq310djhsrma0ctv08bfgqahg.webp"
    },
    {
      number: "3",
      title: "Customize Experience",
      description: "Fine-tune behavior and branding",
      icon: Settings,
      features: [
        "Set custom prompts and goals",
        "Configure lead capture scenarios",
        "Custom branding and visual identity",
        "Design pre and post-conversation experiences"
      ],
      image: "/home-page-imagery/replicate-prediction-ta1cdk9s49rme0ctv04b1caef8.webp"
    },
    {
      number: "4",
      title: "Deploy Everywhere",
      description: "Share your Genie across all channels",
      icon: Globe,
      features: [
        "Direct links and landing pages",
        "Embed on websites",
        "Share via QR codes",
        "Set up phone numbers"
      ],
      image: "/home-page-imagery/replicate-prediction-3ymns27nhsrm80ctv0ptrjgqmw.webp"
    }
  ];

  const deploymentOptions = [
    {
      title: "Web Embed",
      description: "Add to any website with a simple code snippet",
      image: "/optimized/how-it-works/0qen528g1xrmr0cvqajbr3bhsr_1768362905970.webp",
      features: ["Customizable widget", "Floating or inline", "Mobile responsive"]
    },
    {
      title: "QR Codes",
      description: "Perfect for physical locations and print materials",
      image: "/optimized/how-it-works/71j8z79yn1rmt0cvqakakfhxdg_1768363055958.webp",
      features: ["Dynamic QR generation", "Trackable scans", "Custom branding"]
    },
    {
      title: "Direct Links",
      description: "Share dedicated URLs for your Genie",
      image: "/optimized/how-it-works/11v945y5a1rmw0cvqakv0wzr40_1768363158255.webp",
      features: ["Custom domains", "SEO optimized", "Social sharing ready"]
    },
    {
      title: "Landing Pages",
      description: "Beautiful standalone pages for campaigns",
      image: "/optimized/how-it-works/ny9b88r8z9rmt0cvqawsfe9s8w_1768364284128.webp",
      features: ["Fully branded", "Lead capture forms", "Analytics tracking"]
    },
    {
      title: "Phone Numbers",
      description: "Connect Genies to phone lines",
      image: "/optimized/how-it-works/c4vf875b3hrmy0cvqarrccnp98_1768363801538.webp",
      features: ["Local numbers", "Call routing", "Voicemail transcription"]
    },
    {
      title: "Email Integration",
      description: "Automated follow-ups and summaries",
      image: "/optimized/how-it-works/sbwhftbj9xrmr0cvqamv0y2qy8_1768363262656.webp",
      features: ["Conversation reports", "Lead notifications", "Custom templates"]
    }
  ];

  const powerFeatures = [
    {
      title: "Intelligence & Understanding",
      description: "Advanced AI capabilities that understand context, intent, and nuance",
      image: "/optimized/new-imagery/contentlead-image-235cfd6f-2fa5-4c7b-b24e-993234996dc0.webp",
      features: [
        "AI-powered understanding",
        "40+ languages supported",
        "Natural voice interaction",
        "Context retention"
      ]
    },
    {
      title: "Complete Customization",
      description: "Shape every aspect to match your brand and business needs",
      image: "/optimized/new-imagery/contentlead-image-4b9d61c6-af77-4853-8ee0-57ff184d0d64.webp",
      features: [
        "Full brand customization",
        "Goal-driven conversations",
        "Custom knowledge base",
        "Personality tuning"
      ]
    },
    {
      title: "Analytics & Insights",
      description: "Deep insights into every conversation and customer interaction",
      image: "/optimized/new-imagery/contentlead-image-707c60a6-9e12-4e16-b3cc-91a4696c7b4f.webp",
      features: [
        "Conversation insights",
        "Lead tracking",
        "Real-time monitoring",
        "Performance metrics"
      ]
    },
    {
      title: "Enterprise Security",
      description: "Bank-grade security and compliance for your peace of mind",
      image: "/optimized/new-imagery/contentlead-image-c10b3f29-a5df-4c55-b9cb-ee6f8fdf76b6.webp",
      features: [
        "Enterprise security",
        "Data privacy controls",
        "Access management",
        "GDPR compliant"
      ]
    }
  ];

  const postConversationFeatures = [
    {
      title: "Lead Capture",
      description: "Collect valuable customer information",
      image: "/optimized/new-imagery/contentlead-image-235cfd6f-2fa5-4c7b-b24e-993234996dc0.webp",
      details: ["Progressive profiling", "Custom forms", "Conditional fields"]
    },
    {
      title: "Smart Redirects",
      description: "Guide users to the right next step",
      image: "/optimized/new-imagery/contentlead-image-707c60a6-9e12-4e16-b3cc-91a4696c7b4f.webp",
      details: ["Conditional routing", "UTM tracking", "A/B testing"]
    },
    {
      title: "Email Follow-ups",
      description: "Automated conversation summaries",
      image: "/optimized/new-imagery/contentlead-image-fe16751a-9a7d-4a49-a28a-5c609b258ffd.webp",
      details: ["Instant transcripts", "Action items", "Lead alerts"]
    },
    {
      title: "Analytics & Insights",
      description: "Understand every conversation",
      image: "/optimized/new-imagery/contentlead-image-fba11cf3-9d3f-4e9e-b7db-1847e8070dc5.webp",
      details: ["Sentiment analysis", "Topic extraction", "Performance metrics"]
    }
  ];

  return (
    <>
      <Helmet>
        <title>How Help Genie Works | Setup, Knowledge and Deployment</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={getCanonicalUrl("/how-it-works")} />
        <meta property="og:title" content="How Help Genie Works | Setup, Knowledge and Deployment" />
        <meta property="og:description" content={description} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={getCanonicalUrl("/how-it-works")} />
        <meta property="og:image" content={getCanonicalUrl("/helpgenie_logo.png")} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="How Help Genie Works | Setup, Knowledge and Deployment" />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content={getCanonicalUrl("/helpgenie_logo.png")} />
        <script type="application/ld+json">{JSON.stringify(howToSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(organizationSchema)}</script>
      </Helmet>

      <div className="min-h-screen w-full flex flex-col bg-[#F9FAFC]">
        <HomeNav />
      
      <main className="flex-1 w-full">
        {/* Hero Section */}
        <section className="relative min-h-[600px] flex items-center justify-center py-20 px-4 sm:px-6 lg:px-8">
          {/* Background Image */}
          <div className="absolute inset-0 z-0">
            <img
              src="/optimized/tmphn9da0yi.webp"
              alt="How Genie Works Background"
              className="w-full h-full object-cover"
            />
            {/* Overlay for better text readability */}
            <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/60 to-black/70"></div>
            <div className="absolute inset-0 bg-black/20 backdrop-blur-sm"></div>
          </div>
          
          {/* Content */}
          <div className="relative z-10 max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold tracking-tight text-white mb-6 drop-shadow-2xl">
                How Help Genie Works
              </h1>
              <p className="text-xl text-gray-100 max-w-3xl mx-auto drop-shadow-lg">
                From setup to deployment: discover the complete technical capabilities of your voice agent
              </p>
            </div>
            
            <div className="mt-12 flex justify-center gap-4">
              <a 
                href="/signup"
                className="px-8 py-3 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-300 text-lg"
              >
                Create Your First Genie
                <ArrowRight className="ml-2 h-5 w-5 inline" />
              </a>
              <a 
                href="/playbooks"
                className="px-8 py-3 bg-white/10 text-white hover:text-gray-100 font-semibold rounded-lg border-2 border-white/50 hover:border-white/70 hover:bg-white/20 transition-all duration-300 text-lg backdrop-blur-sm"
              >
                View Use Cases
              </a>
            </div>
            <div className="mt-6 flex flex-wrap justify-center gap-x-5 gap-y-3 text-sm text-gray-200">
              <a href="/help-genie-for-business-solutions" className="hover:text-white transition-colors">
                Explore solutions
              </a>
              <a href="/playbooks" className="hover:text-white transition-colors">
                Playbooks
              </a>
              <a href="/pricing" className="hover:text-white transition-colors">
                Pricing
              </a>
            </div>
          </div>
        </section>

        {/* Setup Process */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
                Four Steps to Your Perfect Genie
              </h2>
              <p className="text-lg text-gray-600">
                Get up and running in minutes with our streamlined setup process
              </p>
            </div>

            <div className="space-y-24">
              {setupSteps.map((step, index) => (
                <div 
                  key={index}
                  className={`flex flex-col lg:flex-row gap-12 items-center ${
                    index % 2 === 1 ? 'lg:flex-row-reverse' : ''
                  }`}
                >
                  <div className="lg:w-1/2">
                    <div className="flex items-center mb-6">
                      <div className="w-12 h-12 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-full flex items-center justify-center font-bold text-xl mr-4">
                        {step.number}
                      </div>
                      <h3 className="text-2xl font-bold text-gray-900">
                        {step.title}
                      </h3>
                    </div>
                    <p className="text-lg text-gray-600 mb-6">
                      {step.description}
                    </p>
                    <ul className="space-y-3">
                      {step.features.map((feature, fIndex) => (
                        <li key={fIndex} className="flex items-start">
                          <CheckCircle2 className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                          <span className="text-gray-700">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="lg:w-1/2">
                    <Card className="overflow-hidden shadow-xl">
                      <img 
                        src={step.image} 
                        alt={step.title}
                        className="w-full h-80 object-cover"
                      />
                    </Card>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Deployment Options */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
                Deploy Your Genie Everywhere
              </h2>
              <p className="text-lg text-gray-600">
                Multiple deployment options to reach your customers wherever they are
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {deploymentOptions.map((option, index) => (
                <Card key={index} className="overflow-hidden hover:shadow-lg transition-shadow">
                  <div className="h-48 overflow-hidden bg-white">
                    <img
                      src={option.image}
                      alt={option.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">
                      {option.title}
                    </h3>
                    <p className="text-gray-600 mb-4">
                      {option.description}
                    </p>
                    <ul className="space-y-2">
                      {option.features.map((feature, fIndex) => (
                        <li key={fIndex} className="flex items-center text-sm text-gray-700">
                          <CheckCircle2 className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Combined Features Section */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-purple-50 to-blue-50">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                Powerful Features & Capabilities
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                Enterprise-grade capabilities and post-conversation intelligence built into every Genie
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {/* Feature 1 */}
              <div className="group bg-white border border-gray-200 rounded-lg p-6 hover:border-purple-400 hover:shadow-lg transition-all duration-300">
                <div className="border-l-4 border-purple-500 pl-4">
                  <h3 className="font-bold text-gray-900 mb-3 text-xl group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-purple-600 group-hover:to-blue-600 transition-all">
                    Voice Intelligence
                  </h3>
                  <p className="text-base text-gray-600 leading-relaxed mb-4">
                    Advanced voice recognition with context understanding and natural conversation flow
                  </p>
                  <ul className="space-y-2">
                    <li className="flex items-start text-sm text-gray-700">
                      <span className="w-2 h-2 bg-purple-500 rounded-full mr-2 mt-1.5 flex-shrink-0" />
                      40+ languages supported
                    </li>
                    <li className="flex items-start text-sm text-gray-700">
                      <span className="w-2 h-2 bg-purple-500 rounded-full mr-2 mt-1.5 flex-shrink-0" />
                      Context retention
                    </li>
                    <li className="flex items-start text-sm text-gray-700">
                      <span className="w-2 h-2 bg-purple-500 rounded-full mr-2 mt-1.5 flex-shrink-0" />
                      Natural voice interaction
                    </li>
                  </ul>
                </div>
              </div>

              {/* Feature 2 */}
              <div className="group bg-white border border-gray-200 rounded-lg p-6 hover:border-purple-400 hover:shadow-lg transition-all duration-300">
                <div className="border-l-4 border-purple-500 pl-4">
                  <h3 className="font-bold text-gray-900 mb-3 text-xl group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-purple-600 group-hover:to-blue-600 transition-all">
                    Complete Customization
                  </h3>
                  <p className="text-base text-gray-600 leading-relaxed mb-4">
                    Shape every aspect to match your brand and business needs perfectly
                  </p>
                  <ul className="space-y-2">
                    <li className="flex items-start text-sm text-gray-700">
                      <span className="w-2 h-2 bg-purple-500 rounded-full mr-2 mt-1.5 flex-shrink-0" />
                      Full brand customization
                    </li>
                    <li className="flex items-start text-sm text-gray-700">
                      <span className="w-2 h-2 bg-purple-500 rounded-full mr-2 mt-1.5 flex-shrink-0" />
                      Goal-driven conversations
                    </li>
                    <li className="flex items-start text-sm text-gray-700">
                      <span className="w-2 h-2 bg-purple-500 rounded-full mr-2 mt-1.5 flex-shrink-0" />
                      Personality tuning
                    </li>
                  </ul>
                </div>
              </div>

              {/* Feature 3 */}
              <div className="group bg-white border border-gray-200 rounded-lg p-6 hover:border-purple-400 hover:shadow-lg transition-all duration-300">
                <div className="border-l-4 border-purple-500 pl-4">
                  <h3 className="font-bold text-gray-900 mb-3 text-xl group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-purple-600 group-hover:to-blue-600 transition-all">
                    Lead Capture
                  </h3>
                  <p className="text-base text-gray-600 leading-relaxed mb-4">
                    Collect valuable customer information through natural conversation
                  </p>
                  <ul className="space-y-2">
                    <li className="flex items-start text-sm text-gray-700">
                      <span className="w-2 h-2 bg-purple-500 rounded-full mr-2 mt-1.5 flex-shrink-0" />
                      Progressive profiling
                    </li>
                    <li className="flex items-start text-sm text-gray-700">
                      <span className="w-2 h-2 bg-purple-500 rounded-full mr-2 mt-1.5 flex-shrink-0" />
                      Custom forms
                    </li>
                    <li className="flex items-start text-sm text-gray-700">
                      <span className="w-2 h-2 bg-purple-500 rounded-full mr-2 mt-1.5 flex-shrink-0" />
                      Conditional fields
                    </li>
                  </ul>
                </div>
              </div>

              {/* Feature 4 */}
              <div className="group bg-white border border-gray-200 rounded-lg p-6 hover:border-purple-400 hover:shadow-lg transition-all duration-300">
                <div className="border-l-4 border-purple-500 pl-4">
                  <h3 className="font-bold text-gray-900 mb-3 text-xl group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-purple-600 group-hover:to-blue-600 transition-all">
                    Smart Redirects
                  </h3>
                  <p className="text-base text-gray-600 leading-relaxed mb-4">
                    Guide users to the right next step based on conversation outcomes
                  </p>
                  <ul className="space-y-2">
                    <li className="flex items-start text-sm text-gray-700">
                      <span className="w-2 h-2 bg-purple-500 rounded-full mr-2 mt-1.5 flex-shrink-0" />
                      Conditional routing
                    </li>
                    <li className="flex items-start text-sm text-gray-700">
                      <span className="w-2 h-2 bg-purple-500 rounded-full mr-2 mt-1.5 flex-shrink-0" />
                      UTM tracking
                    </li>
                    <li className="flex items-start text-sm text-gray-700">
                      <span className="w-2 h-2 bg-purple-500 rounded-full mr-2 mt-1.5 flex-shrink-0" />
                      A/B testing
                    </li>
                  </ul>
                </div>
              </div>

              {/* Feature 5 */}
              <div className="group bg-white border border-gray-200 rounded-lg p-6 hover:border-purple-400 hover:shadow-lg transition-all duration-300">
                <div className="border-l-4 border-purple-500 pl-4">
                  <h3 className="font-bold text-gray-900 mb-3 text-xl group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-purple-600 group-hover:to-blue-600 transition-all">
                    Analytics & Insights
                  </h3>
                  <p className="text-base text-gray-600 leading-relaxed mb-4">
                    Deep insights into every conversation and customer interaction
                  </p>
                  <ul className="space-y-2">
                    <li className="flex items-start text-sm text-gray-700">
                      <span className="w-2 h-2 bg-purple-500 rounded-full mr-2 mt-1.5 flex-shrink-0" />
                      Sentiment analysis
                    </li>
                    <li className="flex items-start text-sm text-gray-700">
                      <span className="w-2 h-2 bg-purple-500 rounded-full mr-2 mt-1.5 flex-shrink-0" />
                      Topic extraction
                    </li>
                    <li className="flex items-start text-sm text-gray-700">
                      <span className="w-2 h-2 bg-purple-500 rounded-full mr-2 mt-1.5 flex-shrink-0" />
                      Performance metrics
                    </li>
                  </ul>
                </div>
              </div>

              {/* Feature 6 */}
              <div className="group bg-white border border-gray-200 rounded-lg p-6 hover:border-purple-400 hover:shadow-lg transition-all duration-300">
                <div className="border-l-4 border-purple-500 pl-4">
                  <h3 className="font-bold text-gray-900 mb-3 text-xl group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-purple-600 group-hover:to-blue-600 transition-all">
                    Email Follow-ups
                  </h3>
                  <p className="text-base text-gray-600 leading-relaxed mb-4">
                    Automated conversation summaries and lead notifications
                  </p>
                  <ul className="space-y-2">
                    <li className="flex items-start text-sm text-gray-700">
                      <span className="w-2 h-2 bg-purple-500 rounded-full mr-2 mt-1.5 flex-shrink-0" />
                      Instant transcripts
                    </li>
                    <li className="flex items-start text-sm text-gray-700">
                      <span className="w-2 h-2 bg-purple-500 rounded-full mr-2 mt-1.5 flex-shrink-0" />
                      Action items
                    </li>
                    <li className="flex items-start text-sm text-gray-700">
                      <span className="w-2 h-2 bg-purple-500 rounded-full mr-2 mt-1.5 flex-shrink-0" />
                      Lead alerts
                    </li>
                  </ul>
                </div>
              </div>

              {/* Feature 7 */}
              <div className="group bg-white border border-gray-200 rounded-lg p-6 hover:border-purple-400 hover:shadow-lg transition-all duration-300">
                <div className="border-l-4 border-purple-500 pl-4">
                  <h3 className="font-bold text-gray-900 mb-3 text-xl group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-purple-600 group-hover:to-blue-600 transition-all">
                    Enterprise Security
                  </h3>
                  <p className="text-base text-gray-600 leading-relaxed mb-4">
                    Bank-grade security and compliance for your peace of mind
                  </p>
                  <ul className="space-y-2">
                    <li className="flex items-start text-sm text-gray-700">
                      <span className="w-2 h-2 bg-purple-500 rounded-full mr-2 mt-1.5 flex-shrink-0" />
                      Data privacy controls
                    </li>
                    <li className="flex items-start text-sm text-gray-700">
                      <span className="w-2 h-2 bg-purple-500 rounded-full mr-2 mt-1.5 flex-shrink-0" />
                      Access management
                    </li>
                    <li className="flex items-start text-sm text-gray-700">
                      <span className="w-2 h-2 bg-purple-500 rounded-full mr-2 mt-1.5 flex-shrink-0" />
                      GDPR compliant
                    </li>
                  </ul>
                </div>
              </div>

              {/* Feature 8 */}
              <div className="group bg-white border border-gray-200 rounded-lg p-6 hover:border-purple-400 hover:shadow-lg transition-all duration-300">
                <div className="border-l-4 border-purple-500 pl-4">
                  <h3 className="font-bold text-gray-900 mb-3 text-xl group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-purple-600 group-hover:to-blue-600 transition-all">
                    Real-time Monitoring
                  </h3>
                  <p className="text-base text-gray-600 leading-relaxed mb-4">
                    Track conversations and performance as they happen
                  </p>
                  <ul className="space-y-2">
                    <li className="flex items-start text-sm text-gray-700">
                      <span className="w-2 h-2 bg-purple-500 rounded-full mr-2 mt-1.5 flex-shrink-0" />
                      Live conversation feed
                    </li>
                    <li className="flex items-start text-sm text-gray-700">
                      <span className="w-2 h-2 bg-purple-500 rounded-full mr-2 mt-1.5 flex-shrink-0" />
                      Performance tracking
                    </li>
                    <li className="flex items-start text-sm text-gray-700">
                      <span className="w-2 h-2 bg-purple-500 rounded-full mr-2 mt-1.5 flex-shrink-0" />
                      Alert notifications
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-900">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
              Ready to Build Your Genie?
            </h2>
            <p className="text-xl text-gray-300 mb-8">
              Join thousands of businesses transforming their customer experience with voice technology
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="/signup"
                className="px-8 py-3 bg-white text-gray-900 hover:bg-gray-100 font-semibold rounded-lg shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-300 text-lg inline-block text-center"
              >
                Start Free Trial
                <ArrowRight className="ml-2 h-5 w-5 inline" />
              </a>
              <a 
                href="/live-agent/business-onboarding"
                className="px-8 py-3 text-white border-2 border-white hover:bg-white hover:text-gray-900 font-semibold rounded-lg transition-all duration-300 text-lg inline-block text-center"
              >
                Test Out a Demo
              </a>
            </div>
          </div>
        </section>
      </main>

        <HomeFooter />
        <GenieEmbed position="bottom-right" />
      </div>
    </>
  );
};

export default HowGenieWorks;
